import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "h-11 bg-white border-sand focus-visible:ring-gold rounded-none text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-1.5";

export const SettingsForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => {
      const c = r.data || {};
      setForm({
        phone: c.phone || "",
        email: c.email || "",
        hoursEn: c.hours?.en || "",
        hoursEs: c.hours?.es || "",
        stats: (c.stats || []).map((s) => ({ value: s.value, labelEn: s.label?.en || "", labelEs: s.label?.es || "" })),
        marqueeEn: (c.marquee?.en || []).join(", "),
        marqueeEs: (c.marquee?.es || []).join(", "),
      });
    }).catch(() => toast.error(t.form.error));
  }, []);

  if (!form) return <p className="text-[#8C96A4] text-sm">…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setStat = (i, k) => (e) => {
    const stats = [...form.stats];
    stats[i] = { ...stats[i], [k]: e.target.value };
    setForm({ ...form, stats });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem("cv_admin_token");
    const payload = {
      phone: form.phone,
      email: form.email,
      hours: { en: form.hoursEn, es: form.hoursEs },
      stats: form.stats.map((s) => ({ value: s.value, label: { en: s.labelEn, es: s.labelEs } })),
      marquee: {
        en: form.marqueeEn.split(",").map((x) => x.trim()).filter(Boolean),
        es: form.marqueeEs.split(",").map((x) => x.trim()).filter(Boolean),
      },
    };
    try {
      await axios.put(`${API}/admin/content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.admin.contentSaved);
    } catch {
      toast.error(t.form.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="settings-form" onSubmit={submit} className="space-y-10 max-w-3xl">
      <div>
        <p className="font-serif text-xl text-ink mb-5 flex items-center gap-3"><span className="text-gold text-sm">01</span>{t.admin.contactInfoTitle}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t.admin.phoneLabel}</label>
            <Input data-testid="settings-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.emailLabel}</label>
            <Input data-testid="settings-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.hoursEn}</label>
            <Input data-testid="settings-hours-en-input" value={form.hoursEn} onChange={set("hoursEn")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.hoursEs}</label>
            <Input data-testid="settings-hours-es-input" value={form.hoursEs} onChange={set("hoursEs")} className={inputCls} />
          </div>
        </div>
      </div>

      <div>
        <p className="font-serif text-xl text-ink mb-5 flex items-center gap-3"><span className="text-gold text-sm">02</span>{t.admin.statsTitle}</p>
        <div className="space-y-4">
          {form.stats.map((s, i) => (
            <div key={i} data-testid={`settings-stat-row-${i}`} className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white border border-sand/80 p-4">
              <div>
                <label className={labelCls}>{t.admin.valueLabel}</label>
                <Input data-testid={`settings-stat-value-${i}`} value={s.value} onChange={setStat(i, "value")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t.admin.labelEn}</label>
                <Input data-testid={`settings-stat-label-en-${i}`} value={s.labelEn} onChange={setStat(i, "labelEn")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t.admin.labelEs}</label>
                <Input data-testid={`settings-stat-label-es-${i}`} value={s.labelEs} onChange={setStat(i, "labelEs")} className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-serif text-xl text-ink mb-5 flex items-center gap-3"><span className="text-gold text-sm">03</span>{t.admin.marqueeTitle}</p>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>{t.admin.marqueeEn}</label>
            <Textarea data-testid="settings-marquee-en-input" rows={2} value={form.marqueeEn} onChange={set("marqueeEn")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-sm" />
          </div>
          <div>
            <label className={labelCls}>{t.admin.marqueeEs}</label>
            <Textarea data-testid="settings-marquee-es-input" rows={2} value={form.marqueeEs} onChange={set("marqueeEs")} className="bg-white border-sand focus-visible:ring-gold rounded-none text-sm" />
          </div>
        </div>
      </div>

      <button data-testid="settings-save-btn" type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-8 py-3.5 transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.saveSettings}
      </button>
    </form>
  );
};
